import React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Table from '@mui/joy/Table';
import Link from '@mui/joy/Link';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {getPages} from '../../common/pages/pages';
import {UserBlockRuleRow, getBlockRuleLabels, BlockRule} from '../../models/userBlock';
import {userBlockRulesListApi, userBlockRulesSetApi} from './userBlockRulesApi';
import {NotificationService} from '../../common/notification';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import Checkbox from '@mui/joy/Checkbox';
import {useLocation, useNavigate} from 'react-router-dom';
import { NavigationTabs } from '../../components/tab/NavigationTabs';
import { AutoHoldPanel } from './AutoHoldPanel';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/joy/Tooltip';
import ConfirmDialog from '../../components/confirmDialog/ConfirmDialog';
import { formatDateTime } from '../../common/dateTime';

export function UserBlocksPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = React.useState<UserBlockRuleRow[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [steamIdFilter, setSteamIdFilter] = React.useState<string>('');
    const [loading, setLoading] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [isEdit, setIsEdit] = React.useState(false);
    const [addUserId, setAddUserId] = React.useState<string>('');
    const [addComment, setAddComment] = React.useState<string>('');
    const [addRuleFlags, setAddRuleFlags] = React.useState<number>(0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState<boolean>(false);
    const [pendingDeleteUserId, setPendingDeleteUserId] = React.useState<string>('');
    const [saving, setSaving] = React.useState<boolean>(false);

    const blocksTabActive = !(location.hash || '').replace('#', '').trim().toLowerCase().startsWith('autohold');

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            const userId = steamIdFilter && /^\d+$/.test(steamIdFilter) ? BigInt(steamIdFilter) : undefined;
            const result = await userBlockRulesListApi(page, 30, userId);
            if (result.result) {
                const res = result.value!;
                setItems(res.items);
                setPageCount(Math.ceil(res.total / 30));
            } else {
                NotificationService.alertError('Не удалось загрузить список блокировок');
            }
        } finally {
            setLoading(false);
        }
    }, [page, steamIdFilter]);

    React.useEffect(() => {
        if (!blocksTabActive) {
            return;
        }
        loadData().catch(console.error);
        // loadData меняется при вводе фильтра — список грузим по «Поиск», смене страницы и входу на вкладку
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocksTabActive, page]);

    const applyFilter = React.useCallback(async () => {
        if (page !== 1) {
            setPage(1);
        } else {
            await loadData();
        }
    }, [page, loadData]);

    const handleAdd = React.useCallback(async () => {
        if (saving) return;
        setSaving(true);
        try {
            const userId = BigInt(addUserId);
            if (!isEdit && addRuleFlags === 0) {
                NotificationService.alertError('Выберите хотя бы одно правило');
                return;
            }
            const res = await userBlockRulesSetApi(userId, addRuleFlags, addComment);
            if (res.result) {
                setAddOpen(false);
                setIsEdit(false);
                setAddUserId('');
                setAddComment('');
                setAddRuleFlags(0);
                await loadData();
                NotificationService.alertSuccess(isEdit ? 'Блокировка обновлена' : 'Блокировка добавлена');
            } else {
                NotificationService.alertError('Не удалось сохранить блокировку');
            }
        } catch (e) {
            NotificationService.alertError('Неверный SteamId');
        } finally {
            setSaving(false);
        }
    }, [saving, addUserId, addRuleFlags, addComment, isEdit, loadData]);

    const startEdit = React.useCallback((row: UserBlockRuleRow) => {
        setIsEdit(true);
        setAddUserId(row.userId.toString());
        setAddComment(row.comment || '');
        setAddRuleFlags(row.rule);
        setAddOpen(true);
    }, []);

    const requestDelete = React.useCallback((userId: string) => {
        setPendingDeleteUserId(userId);
        setDeleteConfirmOpen(true);
    }, []);

    const confirmDelete = React.useCallback(async () => {
        if (!pendingDeleteUserId) return;
        const userId = BigInt(pendingDeleteUserId);
        const res = await userBlockRulesSetApi(userId, BlockRule.None, '');
        setDeleteConfirmOpen(false);
        setPendingDeleteUserId('');
        if (res.result) {
            NotificationService.alertSuccess('Блокировка удалена');
            await loadData();
        } else {
            NotificationService.alertError('Не удалось удалить блокировку');
        }
    }, [pendingDeleteUserId, loadData]);

    const rules = React.useMemo(() => getBlockRuleLabels(), []);
    const allRulesMask = React.useMemo(() => rules.reduce((acc, r) => acc | r.value, 0), [rules]);
    const groupedRules = React.useMemo(() => {
        const byValue = new Map(rules.map(r => [r.value, r] as const));
        return [
            {
                title: 'Пополнение и Вывод',
                items: [BlockRule.Deposit, BlockRule.Withdraw].map(v => byValue.get(v)).filter(Boolean) as {value:number,label:string}[],
            },
            {
                title: 'Покупка и продажа',
                items: [BlockRule.Buy, BlockRule.Sell].map(v => byValue.get(v)).filter(Boolean) as {value:number,label:string}[],
            },
            {
                title: 'Обмены',
                items: [BlockRule.ReceiveItems].map(v => byValue.get(v)).filter(Boolean) as {value:number,label:string}[],
            },
            {
                title: 'Страницы',
                items: [BlockRule.OperationsHistoryPage].map(v => byValue.get(v)).filter(Boolean) as {value:number,label:string}[],
            },
        ];
    }, [rules]);

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: { xs: 2, md: 6 },
                pt: {
                    xs: 'calc(12px + var(--Header-height))',
                    sm: 'calc(12px + var(--Header-height))',
                    md: 3,
                },
                pb: { xs: 2, sm: 2, md: 3 },
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                height: '100dvh',
                gap: 1,
            }}
        >
            <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                Блокировки
            </Typography>
            <NavigationTabs
                tabs={[
                    {
                        tabSlug: 'blocks',
                        tabTitle: 'Блокировки',
                        tabContent: (
                            <>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    p: 2,
                    bgcolor: 'background.level1',
                    borderRadius: '8px',
                    mb: 2
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 220 }}>
                    <FormLabel>SteamId</FormLabel>
                    <Input
                        placeholder="Введите SteamId..."
                        value={steamIdFilter}
                        onChange={(e) => setSteamIdFilter(e.target.value)}
                    />
                </FormControl>
                <Button onClick={() => applyFilter().catch(console.error)} size="sm">Поиск</Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    startDecorator={<AddIcon/>}
                    color="primary"
                    size="sm"
                    onClick={() => setAddOpen(true)}
                >
                    Добавить
                </Button>
            </Box>

            {/* Desktop table */}
            <Sheet variant="outlined" sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Table>
                    <thead>
                    <tr>
                        <th>SteamId</th>
                        <th>Правила</th>
                        <th>Комментарий</th>
                        <th>Создано</th>
                        <th>Обновлено</th>
                        <th style={{ width: 120 }}>Действия</th>
                    </tr>
                    </thead>
                    <tbody>
                    {items.map((x, i) => (
                        <tr key={i}>
                            <td>
                                <Link underline="hover" onClick={() => navigate(`/app/users/${x.userId.toString()}`)}>
                                    {x.userId.toString()}
                                </Link>
                            </td>
                            <td>{rules.filter(r => (x.rule & r.value) === r.value).map(r => r.label).join(', ')}</td>
                            <td>{x.comment}</td>
                            <td>{formatDateTime(x.created)}</td>
                            <td>{formatDateTime(x.updated)}</td>
                            <td>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Редактировать">
                                        <IconButton size="sm" variant="outlined" onClick={() => startEdit(x)}>
                                            <EditIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Удалить">
                                        <IconButton size="sm" variant="outlined" color="danger" onClick={() => requestDelete(x.userId.toString())}>
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ p: 1 }}>
                    <Button variant="plain" disabled={page <= 1 || loading} onClick={() => setPage(1)}>
                        В начало
                    </Button>
                    <IconButton variant="plain" disabled={page <= 1 || loading} onClick={() => setPage(Math.max(1, page - 1))}>
                        <KeyboardArrowLeftIcon/>
                    </IconButton>
                    {getPages(pageCount).map(p => {
                        const isDots = p === '...';
                        const pageNum = isDots ? NaN : Number(p);
                        const isActive = !isDots && pageNum === page;
                        return (
                            <Button
                                key={p}
                                variant={isActive ? 'solid' : 'plain'}
                                disabled={isDots}
                                onClick={() => { if (!isDots) { setPage(pageNum); } }}
                            >
                                {p}
                            </Button>
                        );
                    })}
                    <IconButton variant="plain" disabled={page >= pageCount || loading} onClick={() => setPage(Math.min(pageCount, page + 1))}>
                        <KeyboardArrowRightIcon/>
                    </IconButton>
                </Stack>
            </Sheet>

            {/* Mobile cards */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {items.map((x, i) => (
                    <Box
                        key={i}
                        sx={{
                            bgcolor: 'background.level1',
                            borderRadius: 8,
                            p: 2,
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography level="title-sm" fontWeight="bold">Пользователь</Typography>
                            <Typography level="body-xs" color="neutral">{formatDateTime(x.updated)}</Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Link underline="hover" onClick={() => navigate(`/app/users/${x.userId.toString()}`)}>
                                {x.userId.toString()}
                            </Link>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>Правила</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {getBlockRuleLabels()
                                    .filter(r => (x.rule & r.value) === r.value)
                                    .map(r => (
                                        <Chip key={r.value} size="sm" variant="soft" color="primary">{r.label}</Chip>
                                    ))}
                            </Box>
                        </Box>
                        {x.comment && x.comment.trim() !== '' && (
                            <Box sx={{ mb: 1 }}>
                                <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>Комментарий</Typography>
                                <Typography level="body-sm">{x.comment}</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography level="body-xs" color="neutral">Создано: {formatDateTime(x.created)}</Typography>
                            <Typography level="body-xs" color="neutral">Обновлено: {formatDateTime(x.updated)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                            <Tooltip title="Редактировать">
                                <IconButton size="sm" variant="outlined" onClick={() => startEdit(x)}>
                                    <EditIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Удалить">
                                <IconButton size="sm" variant="outlined" color="danger" onClick={() => requestDelete(x.userId.toString())}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Modal open={addOpen} onClose={() => { setAddOpen(false); setIsEdit(false); }}>
                <ModalDialog>
                    <Typography level="h4">{isEdit ? 'Редактировать блокировку' : 'Добавить блокировку'}</Typography>
                    <FormControl>
                        <FormLabel>SteamId</FormLabel>
                        <Input value={addUserId} onChange={e => setAddUserId(e.target.value)} placeholder="7656119..." disabled={isEdit}/>
                    </FormControl>
                    <div>
                        <FormLabel>Правила</FormLabel>
                        <Stack spacing={1}>
                            <Checkbox
                                label={<Typography fontWeight="lg">Все</Typography>}
                                checked={(addRuleFlags & allRulesMask) === allRulesMask && allRulesMask !== 0}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setAddRuleFlags(checked ? allRulesMask : 0);
                                }}
                            />
                            <Divider/>
                            {/* First two groups side-by-side */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                                {[0,1].map((gi) => {
                                    const grp = groupedRules[gi];
                                    if (!grp) return null;
                                    const groupMask = grp.items.reduce((acc, it) => acc | it.value, 0);
                                    const groupChecked = groupMask !== 0 && (addRuleFlags & groupMask) === groupMask;
                                    return (
                                        <Box key={gi} sx={{ mb: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                {(gi === 0 || gi === 1) ? (
                                                    <Checkbox
                                                        size="sm"
                                                        label={<Typography level="body-sm">{grp.title}</Typography>}
                                                        checked={groupChecked}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setAddRuleFlags(prev => checked ? (prev | groupMask) : (prev & ~groupMask));
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography level="body-sm">
                                                        {grp.title}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Stack spacing={0.5}>
                                                {grp.items.map(r => (
                                                    <Checkbox
                                                        key={r.value}
                                                        label={r.label}
                                                        checked={(addRuleFlags & r.value) === r.value}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setAddRuleFlags(prev => checked ? (prev | r.value) : (prev & ~r.value));
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                            {groupedRules.length > 2 && <Divider sx={{ my: 1 }} />}
                            {/* Remaining groups stacked */}
                            {groupedRules.slice(2).map((grp, giSlice) => {
                                const gi = giSlice + 2;
                                const groupMask = grp.items.reduce((acc, it) => acc | it.value, 0);
                                const groupChecked = groupMask !== 0 && (addRuleFlags & groupMask) === groupMask;
                                return (
                                    <Box key={gi} sx={{ mb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography level="body-sm">
                                                {grp.title}
                                            </Typography>
                                            {/* For now, no group toggle for the remaining groups */}
                                        </Box>
                                        <Stack spacing={0.5}>
                                            {grp.items.map(r => (
                                                <Checkbox
                                                    key={r.value}
                                                    label={r.label}
                                                    checked={(addRuleFlags & r.value) === r.value}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setAddRuleFlags(prev => checked ? (prev | r.value) : (prev & ~r.value));
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                        {gi < groupedRules.length - 1 && <Divider sx={{ my: 1 }} />}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </div>
                    <FormControl>
                        <FormLabel>Комментарий</FormLabel>
                        <Textarea minRows={2} value={addComment} onChange={e => setAddComment(e.target.value)}/>
                    </FormControl>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="plain" onClick={() => { setAddOpen(false); setIsEdit(false); }}>Отмена</Button>
                        <Button onClick={() => handleAdd().catch(console.error)}>Сохранить</Button>
                    </Stack>
                </ModalDialog>
            </Modal>

            <ConfirmDialog
                title="Подтверждение"
                open={deleteConfirmOpen}
                setOpen={setDeleteConfirmOpen as unknown as (e: boolean) => void}
                onConfirm={confirmDelete as unknown as () => void}
            >
                {`Вы действительно хотите удалить блокировку пользователя ${pendingDeleteUserId}?`}
            </ConfirmDialog>
                            </>
                        ),
                    },
                    {
                        tabSlug: 'autohold',
                        tabTitle: 'Автохолд',
                        tabContent: <AutoHoldPanel />,
                    },
                ]}
            />
        </Box>
    );
}


